import { type PropsWithChildren, useContext, useState } from 'react';
import styled from 'styled-components';
import StockIndicator from 'components/shared/stockIndicator';
import { Typography } from 'components/generic/typography';
import { toHtmlContent, format } from 'modules/helpers/strings';
import { useTranslation } from 'react-i18next';
import { ReadMore } from 'autogen/translation-keys/trans-website-shared';
import DeliveryIndicator from 'components/Delivery/DeliveryIndicator/DeliveryIndicator';
import { fetchHTML } from 'modules/helpers/fetch';
import { useQuickView } from 'components/quickView';
import QuickView from 'components/quickView/quickView';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { FontLineHeightNormal, Spacing16, Spacing32, Spacing4 } from 'autogen/design-tokens/tokens';
import { breakpoint, sizes } from 'variables';
import { InlineButtonWithSpinner } from 'components/generic/InlineButton';
import type { IDeliveryInformationList } from '../shared/types';
import { SubscriptionContext } from '../hooks/context';

const Wrapper = styled(Typography.Body)`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing4};
    margin-bottom: 0;
    line-height: ${FontLineHeightNormal};
`;

const Flex = styled.span`
    display: flex;
`;

const QuickViewContent = styled.div`
    padding: ${Spacing16};
    padding-top: 0;

    ${breakpoint.up(sizes.md)} {
        padding: ${Spacing32};
        padding-top: 0;
    }
`;

function LineWithIndicator(props: PropsWithChildren): JSX.Element {
    const { children } = props;

    return (
        <Flex>
            <StockIndicator checkmarkType="Black" useDot />
            <span>{children}</span>
        </Flex>
    );
}

export default function DeliveryInformationList({
    returnInformationText,
    returnInformationLinkAction
}: IDeliveryInformationList): JSX.Element | null {
    const { subscriptionActive } = useContext(SubscriptionContext);
    const { t } = useTranslation();
    const [returnInformationContent, setReturnInformationContent] = useState<string | undefined>(undefined);
    const { toggle } = useQuickView('returnInformationContent');

    if (!(returnInformationText && returnInformationLinkAction)) return null;

    function handleClick(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!returnInformationLinkAction) {
                reject(new Error('No returnInformationUrl provided'));
                return;
            }

            fetchHTML(returnInformationLinkAction.url)
                .then((html) => {
                    setReturnInformationContent(html);
                    toggle(true);
                    resolve();

                    UAEventTracking({
                        eventCategory: 'productPage',
                        eventAction: 'openModal',
                        eventLabel: `${t(ReadMore)} - ReturnInformation Content`
                    });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    return (
        <Wrapper size="S">
            {returnInformationText && returnInformationLinkAction && (
                <>
                    <LineWithIndicator>
                        {toHtmlContent(returnInformationText, 'span')}
                        {'. '}
                        <InlineButtonWithSpinner type="button" onClick={handleClick}>
                            {t(ReadMore)}
                        </InlineButtonWithSpinner>
                    </LineWithIndicator>

                    <QuickView quickViewId="returnInformationContent">
                        <QuickViewContent>{toHtmlContent(returnInformationContent)}</QuickViewContent>
                    </QuickView>
                </>
            )}
        </Wrapper>
    );
}
